import React, { useCallback, useEffect, useState } from 'react';
import {
  View,
  ScrollView,
  Modal,
  ActivityIndicator,
  Platform,
  TouchableOpacity,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Text } from '@/components/ui/text';
import {
  ADMIN_COLORS,
  AdminStatCard,
  AdminStatusPill,
} from '@/components/admin/AdminUI';
import { apiRequest } from '../../services/apiClient';
import { useTranslation } from '../../hooks/useTranslation';
import {
  COMPETENCY_LABEL_KEYS,
  countKey,
  dateLocaleFor,
  lastActivityStatus,
} from './studentDetailFormat';

interface RecentTranscript {
  id: number;
  session_title: string;
  overall_score: number;
  analyzed_at: string;
  competency_scores: any;
}

// GRD-R06/R01: C1/C2 never show a score, even if an older API response holds one.
const UNSCORED_COMPETENCIES = new Set(['demonstrates_ethical_practice', 'embodies_coaching_mindset']);

interface ICFProgress {
  // C1/C2 are never scored (GRD-R06/R01); the API leaves them out.
  competency_averages: {
    establishes_maintains_agreements: number;
    cultivates_trust_safety: number;
    maintains_presence: number;
    listens_actively: number;
    evokes_awareness: number;
    facilitates_client_growth: number;
  };
  total_sessions_analyzed: number;
  overall_average: number;
}

interface DetailedStudentAnalytics {
  student_id: number;
  email: string;
  full_name: string;
  last_activity: string | null;
  transcripts_this_month: number;
  transcripts_total: number;
  chat_messages_total: number;
  documents_uploaded: number;
  avg_icf_score: number | null;
  recent_transcripts: RecentTranscript[];
  icf_progress: ICFProgress | null;
}

interface StudentDetailModalProps {
  visible: boolean;
  studentId: number | null;
  schoolId: number;
  onClose: () => void;
}

const formatDate = (dateStr: string | null, locale: string): string => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatDateTime = (dateStr: string | null, locale: string): string => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getInitials = (name?: string, email?: string): string => {
  const source = (name || email || '?').trim();
  const parts = source.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
};

const scoreTone = (score: number | null | undefined): { fg: string; bg: string; border: string } => {
  if (score == null)
    return {
      fg: ADMIN_COLORS.text.secondary,
      bg: ADMIN_COLORS.surface,
      border: ADMIN_COLORS.border,
    };
  if (score >= 70)
    return {
      fg: ADMIN_COLORS.successText,
      bg: ADMIN_COLORS.successFaint,
      border: ADMIN_COLORS.successBorder,
    };
  if (score >= 50)
    return {
      fg: ADMIN_COLORS.warningText,
      bg: ADMIN_COLORS.warningSoft,
      border: ADMIN_COLORS.warningBorder,
    };
  return {
    fg: ADMIN_COLORS.errorText,
    bg: ADMIN_COLORS.errorSoft,
    border: ADMIN_COLORS.errorBorder,
  };
};

export default function StudentDetailModal({
  visible,
  studentId,
  schoolId,
  onClose,
}: StudentDetailModalProps) {
  const { t, language } = useTranslation();
  const dateLocale = dateLocaleFor(language);
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState<DetailedStudentAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadStudentAnalytics = useCallback(async () => {
    if (!studentId) return;
    try {
      setLoading(true);
      const data = await apiRequest<DetailedStudentAnalytics>({
        endpoint: `/school-analytics/${schoolId}/students/${studentId}/analytics`,
        method: 'GET',
      });
      if (data) setAnalytics(data);
      else setError(t('studentDetail.loadErrorBody'));
    } catch (e: any) {
      let message = t('studentDetail.loadErrorBody');
      if (e.message?.includes('Unauthorized')) {
        message = t('studentDetail.noPermission');
      } else if (e.message?.includes('403') || e.message?.includes('Forbidden')) {
        message = t('studentDetail.accessDenied');
      } else if (e.message) {
        message = e.message;
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [studentId, schoolId, t]);

  useEffect(() => {
    if (visible && studentId) {
      setError(null);
      loadStudentAnalytics();
    } else if (!visible) {
      setAnalytics(null);
      setError(null);
    }
  }, [visible, studentId, loadStudentAnalytics]);

  const activity = lastActivityStatus(analytics?.last_activity ?? null);

  // On web we present this as a right-side drawer (slides in from the right
  // with the underlying screen visible behind a scrim) instead of a centred
  // modal — matches the "Ayşe Yılmaz" archetype mockup. On native, keep the
  // existing pageSheet so iOS/Android continue to feel native.
  const isWeb = Platform.OS === ('web' as any);

  // Body content (header + status pills + scrollable analytics + footer).
  // Hoisted out of the JSX tree so we can drop it inside either the
  // right-side drawer (web) or the existing pageSheet (native).
  const body = (
      <View style={{ flex: 1, backgroundColor: ADMIN_COLORS.surface }}>
        {/* Header */}
        <View
          style={{
            backgroundColor: ADMIN_COLORS.background,
            borderBottomWidth: 1,
            borderBottomColor: ADMIN_COLORS.border,
            paddingHorizontal: 20,
            paddingVertical: 18,
            flexDirection: 'row',
            alignItems: 'center',
            gap: 14,
          }}
        >
          <TouchableOpacity
            onPress={onClose}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel={t('studentDetail.close')}
            style={{
              width: 36,
              height: 36,
              borderRadius: 18,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: ADMIN_COLORS.surface,
            }}
          >
            <MaterialIcons name="close" size={20} color={ADMIN_COLORS.text.primary} />
          </TouchableOpacity>

          <View
            style={{
              width: 48,
              height: 48,
              borderRadius: 14,
              backgroundColor: ADMIN_COLORS.primarySoft,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Text className="text-base font-bold" style={{ color: ADMIN_COLORS.primary }}>
              {getInitials(analytics?.full_name, analytics?.email)}
            </Text>
          </View>

          <View style={{ flex: 1 }}>
            <Text
              className="text-lg font-bold"
              style={{ color: ADMIN_COLORS.text.primary }}
              numberOfLines={1}
            >
              {analytics?.full_name || analytics?.email || t('studentDetail.title')}
            </Text>
            {analytics?.email ? (
              <Text
                className="text-xs"
                style={{ color: ADMIN_COLORS.text.secondary, marginTop: 2 }}
                numberOfLines={1}
              >
                {analytics.email}
              </Text>
            ) : null}
          </View>
        </View>

        {/* Status pills row */}
        {analytics ? (
          <View
            style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 8,
              paddingHorizontal: 20,
              paddingTop: 14,
              paddingBottom: 4,
            }}
          >
            <AdminStatusPill icon="school" tone="primary" label={t('studentDetail.studentPill')} />
            <AdminStatusPill
              icon="schedule"
              tone={activity.tone}
              label={t(activity.key, activity.vars)}
            />
            {analytics.avg_icf_score != null ? (
              <AdminStatusPill
                icon="track-changes"
                tone={analytics.avg_icf_score >= 70 ? 'success' : 'warning'}
                label={t('studentDetail.icfAvgPill', { score: analytics.avg_icf_score.toFixed(0) })}
              />
            ) : null}
          </View>
        ) : null}

        {loading ? (
          <View
            style={{ flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12 }}
          >
            <ActivityIndicator size="large" color={ADMIN_COLORS.primary} />
            <Text className="text-sm" style={{ color: ADMIN_COLORS.text.secondary }}>
              {t('studentDetail.loading')}
            </Text>
          </View>
        ) : error ? (
          <View
            style={{
              flex: 1,
              alignItems: 'center',
              justifyContent: 'center',
              padding: 24,
            }}
          >
            <View
              style={{
                width: 64,
                height: 64,
                borderRadius: 32,
                backgroundColor: ADMIN_COLORS.errorSoft,
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 14,
              }}
            >
              <MaterialIcons
                name="error-outline"
                size={28}
                color={ADMIN_COLORS.errorText}
              />
            </View>
            <Text
              className="text-lg font-bold text-center"
              style={{ color: ADMIN_COLORS.text.primary }}
            >
              {t('studentDetail.loadErrorTitle')}
            </Text>
            <Text
              className="text-sm text-center"
              style={{
                color: ADMIN_COLORS.text.secondary,
                marginTop: 6,
                maxWidth: 360,
              }}
            >
              {error}
            </Text>
            <TouchableOpacity
              onPress={loadStudentAnalytics}
              style={{
                marginTop: 18,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8,
                paddingHorizontal: 18,
                paddingVertical: 10,
                borderRadius: 12,
                backgroundColor: ADMIN_COLORS.primary,
              }}
            >
              <MaterialIcons name="refresh" size={16} color="#FFFFFF" />
              <Text className="text-sm font-semibold" style={{ color: '#FFFFFF' }}>
                {t('studentDetail.tryAgain')}
              </Text>
            </TouchableOpacity>
          </View>
        ) : (
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{ padding: 16, gap: 16 }}
            showsVerticalScrollIndicator={false}
          >
            {analytics ? (
              <>
                {/* Stat cards */}
                <View
                  style={{
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    gap: 12,
                  }}
                >
                  <AdminStatCard
                    icon="event-note"
                    label={t('studentDetail.stats.thisMonth')}
                    value={analytics.transcripts_this_month}
                    hint={t('studentDetail.stats.transcripts')}
                  />
                  <AdminStatCard
                    icon="library-books"
                    label={t('studentDetail.stats.totalSessions')}
                    value={analytics.transcripts_total}
                    tone="primary"
                  />
                  <AdminStatCard
                    icon="forum"
                    label={t('studentDetail.stats.chatMessages')}
                    value={analytics.chat_messages_total}
                  />
                  <AdminStatCard
                    icon="description"
                    label={t('studentDetail.stats.documents')}
                    value={analytics.documents_uploaded}
                  />
                  <AdminStatCard
                    icon="track-changes"
                    label={t('studentDetail.stats.avgIcf')}
                    value={
                      analytics.avg_icf_score != null
                        ? `${analytics.avg_icf_score.toFixed(0)}/100`
                        : '—'
                    }
                    tone={
                      analytics.avg_icf_score == null
                        ? 'primary'
                        : analytics.avg_icf_score >= 70
                        ? 'success'
                        : analytics.avg_icf_score >= 50
                        ? 'warning'
                        : 'error'
                    }
                  />
                </View>

                {/* Two-col content */}
                <View style={{ flexDirection: 'row', gap: 16, flexWrap: 'wrap' }}>
                  {/* Recent transcripts */}
                  <View
                    style={{
                      flex: 1,
                      minWidth: 280,
                      borderRadius: 18,
                      borderWidth: 1,
                      borderColor: ADMIN_COLORS.border,
                      backgroundColor: ADMIN_COLORS.background,
                      padding: 16,
                      gap: 12,
                    }}
                  >
                    <View
                      style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}
                    >
                      <View
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 10,
                          backgroundColor: ADMIN_COLORS.primarySoft,
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <MaterialIcons
                          name="history-edu"
                          size={16}
                          color={ADMIN_COLORS.primary}
                        />
                      </View>
                      <Text
                        className="text-sm font-bold"
                        style={{ color: ADMIN_COLORS.text.primary, flex: 1 }}
                      >
                        {t('studentDetail.recentAnalyses')}
                      </Text>
                      <Text
                        className="text-xs"
                        style={{ color: ADMIN_COLORS.text.muted }}
                      >
                        {t(countKey('studentDetail.itemCount', analytics.recent_transcripts.length), {
                          count: analytics.recent_transcripts.length,
                        })}
                      </Text>
                    </View>

                    {analytics.recent_transcripts.length === 0 ? (
                      <View
                        style={{
                          padding: 18,
                          borderRadius: 12,
                          backgroundColor: ADMIN_COLORS.surface,
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <MaterialIcons
                          name="inbox"
                          size={28}
                          color={ADMIN_COLORS.text.muted}
                        />
                        <Text
                          className="text-sm"
                          style={{ color: ADMIN_COLORS.text.secondary }}
                        >
                          {t('studentDetail.noAnalyses')}
                        </Text>
                      </View>
                    ) : (
                      analytics.recent_transcripts.map((tr, idx) => {
                        const tone = scoreTone(tr.overall_score);
                        return (
                          <View
                            key={tr.id}
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: 10,
                              paddingVertical: 10,
                              borderTopWidth: idx === 0 ? 0 : 1,
                              borderTopColor: ADMIN_COLORS.borderSoft,
                            }}
                          >
                            <View
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: 9,
                                backgroundColor: ADMIN_COLORS.surface,
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <MaterialIcons
                                name="article"
                                size={14}
                                color={ADMIN_COLORS.text.secondary}
                              />
                            </View>
                            <View style={{ flex: 1 }}>
                              <Text
                                className="text-sm font-semibold"
                                style={{ color: ADMIN_COLORS.text.primary }}
                                numberOfLines={1}
                              >
                                {tr.session_title}
                              </Text>
                              <Text
                                className="text-xs"
                                style={{ color: ADMIN_COLORS.text.muted, marginTop: 2 }}
                              >
                                {formatDateTime(tr.analyzed_at, dateLocale)}
                              </Text>
                            </View>
                            <View
                              style={{
                                paddingHorizontal: 10,
                                paddingVertical: 4,
                                borderRadius: 999,
                                backgroundColor: tone.bg,
                                borderWidth: 1,
                                borderColor: tone.border,
                              }}
                            >
                              <Text
                                className="text-xs font-bold"
                                style={{ color: tone.fg }}
                              >
                                {tr.overall_score}/100
                              </Text>
                            </View>
                          </View>
                        );
                      })
                    )}
                  </View>

                  {/* ICF progress */}
                  <View
                    style={{
                      flex: 1,
                      minWidth: 280,
                      borderRadius: 18,
                      borderWidth: 1,
                      borderColor: ADMIN_COLORS.border,
                      backgroundColor: ADMIN_COLORS.background,
                      padding: 16,
                      gap: 14,
                    }}
                  >
                    <View
                      style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}
                    >
                      <View
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 10,
                          backgroundColor: ADMIN_COLORS.successSoft,
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <MaterialIcons
                          name="bar-chart"
                          size={16}
                          color={ADMIN_COLORS.successText}
                        />
                      </View>
                      <Text
                        className="text-sm font-bold"
                        style={{ color: ADMIN_COLORS.text.primary, flex: 1 }}
                      >
                        {t('studentDetail.icfProgressTitle')}
                      </Text>
                    </View>

                    {analytics.icf_progress ? (
                      <>
                        <View
                          style={{
                            padding: 12,
                            borderRadius: 12,
                            backgroundColor: ADMIN_COLORS.primaryFaint,
                            borderWidth: 1,
                            borderColor: ADMIN_COLORS.primaryFaintBorder,
                          }}
                        >
                          <Text
                            className="text-xs font-semibold uppercase"
                            style={{
                              color: ADMIN_COLORS.primary,
                              letterSpacing: 0.4,
                            }}
                          >
                            {t('studentDetail.overallAverage')}
                          </Text>
                          <Text
                            className="text-2xl font-extrabold"
                            style={{
                              color: scoreTone(analytics.icf_progress.overall_average).fg,
                              marginTop: 2,
                            }}
                          >
                            {analytics.icf_progress.overall_average != null
                              ? `${analytics.icf_progress.overall_average.toFixed(1)}/100`
                              : '—'}
                          </Text>
                          <Text
                            className="text-xs"
                            style={{
                              color: ADMIN_COLORS.text.secondary,
                              marginTop: 2,
                            }}
                          >
                            {t(
                              countKey(
                                'studentDetail.basedOnSessions',
                                analytics.icf_progress.total_sessions_analyzed,
                              ),
                              { count: analytics.icf_progress.total_sessions_analyzed },
                            )}
                          </Text>
                        </View>

                        <View style={{ gap: 12 }}>
                          {Object.entries(
                            analytics.icf_progress.competency_averages,
                          ).filter(([key]) => !UNSCORED_COMPETENCIES.has(key)).map(([key, rawScore]) => {
                            const score = rawScore == null ? null : Number(rawScore);
                            const tone = scoreTone(score);
                            return (
                              <View key={key}>
                                <View
                                  style={{
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                    marginBottom: 4,
                                  }}
                                >
                                  <Text
                                    className="text-xs"
                                    style={{
                                      color: ADMIN_COLORS.text.secondary,
                                      flex: 1,
                                      paddingRight: 8,
                                    }}
                                    numberOfLines={1}
                                  >
                                    {COMPETENCY_LABEL_KEYS[key] ? t(COMPETENCY_LABEL_KEYS[key]) : key}
                                  </Text>
                                  <Text
                                    className="text-xs font-bold"
                                    style={{ color: tone.fg }}
                                  >
                                    {score != null ? score.toFixed(0) : '—'}
                                  </Text>
                                </View>
                                <View
                                  style={{
                                    height: 6,
                                    borderRadius: 999,
                                    backgroundColor: ADMIN_COLORS.surface,
                                    overflow: 'hidden',
                                  }}
                                >
                                  <View
                                    style={{
                                      width: `${Math.min(score ?? 0, 100)}%`,
                                      height: '100%',
                                      borderRadius: 999,
                                      backgroundColor: tone.fg,
                                    }}
                                  />
                                </View>
                              </View>
                            );
                          })}
                        </View>
                      </>
                    ) : (
                      <View
                        style={{
                          padding: 18,
                          borderRadius: 12,
                          backgroundColor: ADMIN_COLORS.surface,
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <MaterialIcons
                          name="hourglass-empty"
                          size={28}
                          color={ADMIN_COLORS.text.muted}
                        />
                        <Text
                          className="text-sm text-center"
                          style={{ color: ADMIN_COLORS.text.secondary }}
                        >
                          {t('studentDetail.icfEmpty')}
                        </Text>
                      </View>
                    )}
                  </View>
                </View>

                {/* Last activity footer info */}
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 10,
                    padding: 14,
                    borderRadius: 14,
                    backgroundColor: ADMIN_COLORS.primaryFaint,
                    borderWidth: 1,
                    borderColor: ADMIN_COLORS.primaryFaintBorder,
                  }}
                >
                  <MaterialIcons
                    name="info-outline"
                    size={16}
                    color={ADMIN_COLORS.primary}
                  />
                  <Text
                    className="text-xs flex-1"
                    style={{ color: ADMIN_COLORS.primary }}
                  >
                    {t('studentDetail.lastActivityNote', {
                      date: formatDate(analytics.last_activity, dateLocale),
                    })}
                  </Text>
                </View>
              </>
            ) : null}
          </ScrollView>
        )}

        {/* Footer */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 10,
            padding: 16,
            borderTopWidth: 1,
            borderTopColor: ADMIN_COLORS.border,
            backgroundColor: ADMIN_COLORS.background,
          }}
        >
          <View
            style={{
              flex: 1,
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <MaterialIcons
              name="verified-user"
              size={16}
              color={ADMIN_COLORS.text.secondary}
            />
            <Text className="text-xs" style={{ color: ADMIN_COLORS.text.secondary }}>
              {t('studentDetail.readOnly')}
            </Text>
          </View>
          <TouchableOpacity
            onPress={onClose}
            style={{
              paddingHorizontal: 18,
              paddingVertical: 10,
              borderRadius: 12,
              borderWidth: 1,
              borderColor: ADMIN_COLORS.border,
              backgroundColor: ADMIN_COLORS.background,
            }}
          >
            <Text
              className="text-sm font-semibold"
              style={{ color: ADMIN_COLORS.text.primary }}
            >
              {t('studentDetail.close')}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
  );

  return (
    <Modal
      visible={visible}
      animationType={isWeb ? 'fade' : 'slide'}
      transparent={isWeb}
      presentationStyle={isWeb ? 'overFullScreen' : 'pageSheet'}
      onRequestClose={onClose}
    >
      {isWeb ? (
        <View
          style={{
            flex: 1,
            backgroundColor: 'rgba(15, 23, 42, 0.45)',
            flexDirection: 'row',
            justifyContent: 'flex-end',
          }}
        >
          <TouchableOpacity
            activeOpacity={1}
            onPress={onClose}
            style={{ flex: 1 }}
            accessibilityRole="button"
            accessibilityLabel={t('studentDetail.closeDrawer')}
          />
          <View
            style={{
              width: '100%',
              maxWidth: 940,
              height: '100%',
              backgroundColor: ADMIN_COLORS.surface,
              borderLeftWidth: 1,
              borderLeftColor: ADMIN_COLORS.border,
              shadowColor: '#000',
              shadowOpacity: 0.08,
              shadowRadius: 20,
              shadowOffset: { width: -6, height: 0 },
            }}
          >
            {body}
          </View>
        </View>
      ) : (
        body
      )}
    </Modal>
  );
}
